import React, { useEffect, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import CostCalculator from "@/components/marketing/CostCalculator";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ArrowRight, HelpCircle, FlaskConical, FileText, Calculator, Search, X } from "lucide-react";
import { FAQS, RESEARCH, REFERENCES, CASE_STUDIES } from "@/lib/content";

const TILES = [
  { id: "faqs", icon: HelpCircle, color: "#2C6BAC", title: "FAQs", body: "Plain answers to the questions people ask before, during and after a trial." },
  { id: "research", icon: FlaskConical, color: "#1D9E75", title: "Research", body: "The field studies and cognitive science behind every number we quote, with a full reference list." },
  { id: "case-studies", icon: FileText, color: "#BA7517", title: "Case Studies", body: "Anonymised accounts of how organisations turned aggregate signal into better decisions." },
  { id: "calculator", icon: Calculator, color: "#1A3A5C", title: "Calculator", body: "How we price a FACE, how we reach £500K+, and a calculator for your own numbers." },
];

function SectionHead({ id, eyebrow, title, body }) {
  return (
    <div className="max-w-3xl mb-10">
      <p className="text-xs uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold mb-3">{eyebrow}</p>
      <h2 className="font-display text-3xl lg:text-4xl tracking-tighter text-[#1A1A2E]" data-testid={`resources-${id}-title`}>{title}</h2>
      {body && <p className="mt-4 text-[#555568] leading-relaxed">{body}</p>}
    </div>
  );
}

function buildIndex() {
  const items = [];
  FAQS.forEach((f, i) => items.push({ type: "FAQ", section: "faqs", target: `faq-item-${i}`, open: `faq-${i}`, title: f.q, text: f.a }));
  RESEARCH.forEach((r) => items.push({ type: "Research", section: "research", target: `ref-${r.id}`, title: `${r.prefix || ""}${r.value}${r.suffix} - ${r.label}`, text: `${r.context} ${r.ref}` }));
  CASE_STUDIES.forEach((c, i) => items.push({ type: "Case study", section: "case-studies", target: `case-${i}`, title: c.client, text: `${c.challenge} ${c.insight} ${c.outcome}` }));
  REFERENCES.forEach((r, i) => items.push({ type: "Reference", section: "research", target: `reference-${i}`, title: r, text: "" }));
  items.push({ type: "Calculator", section: "calculator", target: "cost-methodology", title: "How we get to £500K+ and what a FACE costs", text: "calculator off-focus cost refocus WYLO value salary hourly rate employees FACE methodology" });
  return items;
}
const INDEX = buildIndex();
const TYPE_COLOR = { FAQ: "#2C6BAC", Research: "#1D9E75", "Case study": "#BA7517", Reference: "#555568", Calculator: "#1A3A5C" };

function ResourceSearch({ onPick }) {
  const [q, setQ] = useState("");
  const [focus, setFocus] = useState(false);
  const terms = q.toLowerCase().split(/\s+/).filter((t) => t.length > 1);
  const results = terms.length === 0 ? [] : INDEX
    .map((it) => {
      const hay = `${it.title} ${it.text}`.toLowerCase();
      const hits = terms.filter((t) => hay.includes(t)).length;
      const titleHits = terms.filter((t) => it.title.toLowerCase().includes(t)).length;
      return { it, score: hits === terms.length ? hits + titleHits * 2 : hits === 0 ? 0 : hits * 0.5 };
    })
    .filter((r) => r.score > 0).sort((a, b) => b.score - a.score).slice(0, 8);
  const show = focus && terms.length > 0;
  return (
    <div className="relative mt-8 max-w-2xl" data-testid="resources-search">
      <div className="flex items-center gap-3 rounded-full border border-[#E8ECF2] bg-white px-5 h-14 shadow-[0_12px_40px_rgba(26,58,92,0.08)] focus-within:border-[#2C6BAC] transition-colors">
        <Search size={18} className="text-[#2C6BAC] shrink-0" />
        <input value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => setFocus(true)} onBlur={() => setTimeout(() => setFocus(false), 150)}
          onKeyDown={(e) => { if (e.key === "Enter" && results[0]) onPick(results[0].it); if (e.key === "Escape") setQ(""); }}
          placeholder="Ask a question or type a keyword, e.g. 'employer see', 'menopause', '23 minutes'"
          className="flex-1 bg-transparent outline-none text-sm text-[#1A1A2E] placeholder:text-[#555568]/60" data-testid="resources-search-input" aria-label="Search resources" />
        {q && <button type="button" onClick={() => setQ("")} aria-label="Clear" className="text-[#555568] hover:text-[#1A1A2E]" data-testid="resources-search-clear"><X size={16} /></button>}
      </div>
      {show && (
        <div className="absolute left-0 right-0 top-full mt-2 z-30 rounded-xl border border-[#E8ECF2] bg-white shadow-[0_20px_50px_rgba(26,58,92,0.15)] p-2 max-h-96 overflow-y-auto" data-testid="resources-search-results">
          {results.length === 0 ? (
            <p className="px-3 py-3 text-sm text-[#555568]" data-testid="resources-search-empty">No matches. Try another word, or <Link to="/contact" className="text-[#2C6BAC] font-semibold hover:underline">ask us directly</Link>.</p>
          ) : results.map(({ it }, i) => (
            <button key={it.target} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { onPick(it); setQ(""); }}
              className="w-full text-left rounded-lg px-3 py-2.5 hover:bg-[#F8F9FC] flex items-start gap-3" data-testid={`resources-search-result-${i}`}>
              <span className="mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-[10px] uppercase tracking-[0.14em] font-semibold text-white" style={{ backgroundColor: TYPE_COLOR[it.type] }}>{it.type}</span>
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-[#1A1A2E] truncate">{it.title}</span>
                {it.text && <span className="block text-xs text-[#555568] truncate">{it.text}</span>}
              </span>
              <ArrowRight size={14} className="ml-auto mt-1 shrink-0 text-[#2C6BAC]" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Resources() {
  const [params] = useSearchParams();
  const { hash } = useLocation();
  const cite = params.get("cite");
  const [highlight, setHighlight] = useState(null);
  const [flash, setFlash] = useState(null);
  const [openFaq, setOpenFaq] = useState("");

  const pick = (it) => {
    if (it.open) setOpenFaq(it.open);
    setFlash(it.target);
    setTimeout(() => setFlash(null), 6000);
    setTimeout(() => document.getElementById(it.target)?.scrollIntoView({ block: "center", behavior: "smooth" }), 80);
  };

  useEffect(() => {
    if (!cite) return;
    setHighlight(cite);
    const t = setTimeout(() => setHighlight(null), 15000);
    const el = document.getElementById(`ref-${cite}`);
    if (el) setTimeout(() => el.scrollIntoView({ block: "center", behavior: "smooth" }), 150);
    return () => clearTimeout(t);
  }, [cite]);

  useEffect(() => {
    if (!hash || cite) return;
    const el = document.getElementById(hash.slice(1));
    if (el) setTimeout(() => el.scrollIntoView({ block: "start", behavior: "smooth" }), 100);
  }, [hash, cite]);

  return (
    <MarketingLayout>
      <section className="relative overflow-hidden pt-28 pb-16" data-testid="resources-hero">
        <div className="hero-blob" style={{ top: -260, right: -260, opacity: 0.3 }} />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10">
          <p className="text-xs uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold mb-4">Resources</p>
          <h1 className="font-display text-5xl lg:text-7xl tracking-tighter text-[#1A1A2E] leading-[0.95] max-w-3xl">
            The reading room<br /><span className="text-[#1A3A5C]">for the hidden tax on focus.</span>
          </h1>
          <p className="mt-7 text-lg text-[#555568] leading-relaxed max-w-2xl">
            Every claim WYLO makes started as a question: how long does it really take to get back? What does that cost?
            Who pays it? This page is where we show our working - the studies we lean on, the stories clients have shared,
            the answers we give most often, and the arithmetic behind the numbers. Pick a tile to jump straight in, or search.
          </p>
          <ResourceSearch onPick={pick} />
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 lg:px-10 pb-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {TILES.map((t, i) => (
            <motion.a key={t.id} href={`#${t.id}`} data-testid={`resources-tile-${t.id}`}
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08, duration: 0.45 }}
              className="group rounded-xl border border-[#E8ECF2] bg-white p-6 flex flex-col hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(26,58,92,0.12)] transition-[transform,box-shadow]">
              <div className="h-11 w-11 rounded-md text-white flex items-center justify-center" style={{ backgroundColor: t.color }}><t.icon size={18} /></div>
              <h3 className="mt-5 font-display text-xl font-bold text-[#1A1A2E]">{t.title}</h3>
              <p className="mt-2 text-sm text-[#555568] leading-relaxed flex-1">{t.body}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-[#2C6BAC] group-hover:text-[#1A3A5C]">Jump to section <ArrowRight size={12} className="transition-transform group-hover:translate-x-1" /></span>
            </motion.a>
          ))}
        </div>
      </section>

      <section id="faqs" className="scroll-mt-24 bg-white border-y border-[#E8ECF2]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-20">
          <SectionHead id="faqs" eyebrow="FAQs" title="Questions, answered plainly."
            body={<>Can't find what you need? <Link to="/contact" className="text-[#2C6BAC] hover:underline">Contact us</Link> or email <a href="mailto:support@usewylo.com" className="text-[#2C6BAC] hover:underline">support@usewylo.com</a>.</>} />
          <Accordion type="single" collapsible value={openFaq} onValueChange={setOpenFaq} className="rounded-xl border border-[#E8ECF2] bg-[#F8F9FC] px-6 max-w-3xl" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} id={`faq-item-${i}`} data-testid={`faq-item-${i}`} className={flash === `faq-item-${i}` ? "ref-highlight px-3 -mx-3" : ""}>
                <AccordionTrigger className="text-left font-display text-base md:text-lg text-[#1A1A2E]">{f.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-[#555568] leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section id="research" className="scroll-mt-24 max-w-6xl mx-auto px-6 lg:px-10 py-20">
        <SectionHead id="research" eyebrow="Research" title="The science behind the numbers."
          body="Four statistics carry most of WYLO's argument. Each is drawn from peer-reviewed field or laboratory work, summarised here in plain language with the original citation." />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {RESEARCH.map((r) => (
            <article key={r.id} id={`ref-${r.id}`} data-testid={`research-item-${r.id}`}
              className={`rounded-xl border border-[#E8ECF2] bg-white p-7 transition-colors ${highlight === String(r.id) || flash === `ref-${r.id}` ? "ref-highlight" : ""}`}>
              <div className="flex items-baseline gap-3">
                <span className="font-mono text-xs text-[#2C6BAC]">0{r.id}</span>
                <p className="font-display text-3xl tracking-tight text-[#1A1A2E]">{r.prefix}{r.value}{r.suffix}</p>
              </div>
              <p className="mt-1 text-sm font-semibold text-[#1A1A2E]">{r.label}</p>
              <p className="mt-3 text-sm text-[#555568] leading-relaxed">{r.context}</p>
              <p className="mt-3 text-xs italic text-[#555568]">{r.ref}</p>
            </article>
          ))}
        </div>
        <div className="mt-10 rounded-xl border border-[#E8ECF2] bg-white p-8" data-testid="references-list">
          <p className="text-xs uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold mb-4">References</p>
          <ol className="text-xs text-[#555568] leading-relaxed space-y-2 list-decimal pl-4">
            {REFERENCES.map((ref, i) => <li key={ref} id={`reference-${i}`} className={flash === `reference-${i}` ? "ref-highlight px-2 -mx-2" : ""}>{ref}</li>)}
          </ol>
        </div>
      </section>

      <section id="case-studies" className="scroll-mt-24 bg-white border-y border-[#E8ECF2]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <SectionHead id="case-studies" eyebrow="Case studies" title="From aggregate signal to better decisions."
            body="The pattern is consistent: leadership finally has an honest measure of the cost of interruption, and because it was collected without surveillance, everyone in the room trusts it. Here is what clients have done with it so far." />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {CASE_STUDIES.map((c, i) => (
              <article key={i} id={`case-${i}`} className={`rounded-xl border border-[#E8ECF2] bg-[#F8F9FC] p-7 ${flash === `case-${i}` ? "ref-highlight" : ""}`} data-testid={`case-study-${i}`}>
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 shrink-0 rounded-md bg-[#1A3A5C] text-white flex items-center justify-center"><c.icon size={16} /></div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#2C6BAC] font-semibold">Anonymised case study</p>
                    <p className="text-sm font-semibold text-[#1A1A2E]">{c.client}</p>
                  </div>
                </div>
                <dl className="mt-5 space-y-3 text-sm">
                  {[["Challenge", c.challenge], ["What WYLO showed", c.insight], ["Outcome", c.outcome]].map(([k, v]) => (
                    <div key={k}>
                      <dt className="text-[11px] uppercase tracking-[0.16em] text-[#555568] font-semibold">{k}</dt>
                      <dd className="mt-0.5 text-[#1A1A2E] leading-relaxed">{v}</dd>
                    </div>
                  ))}
                </dl>
              </article>
            ))}
          </div>
          <p className="mt-6 text-xs text-[#555568]">Case studies are anonymised composites drawn from client engagements and pilots; figures are client-reported. References protected by NDA.</p>
        </div>
      </section>

      <section id="calculator" className="scroll-mt-24 max-w-6xl mx-auto px-6 lg:px-10 py-20 pb-32">
        <SectionHead id="calculator" eyebrow="Calculator" title="What a FACE costs, and how we get to £500K+." />
        <div id="cost-methodology" className={`rounded-xl border border-[#E8ECF2] bg-white p-7 text-sm text-[#555568] leading-relaxed space-y-3 mb-6 ${flash === "cost-methodology" ? "ref-highlight" : ""}`} data-testid="cost-methodology">
          <p>
            A <strong className="text-[#1A1A2E]">FACE</strong> (Focus or Attention Changing Event) is any drop-in, ping, meeting or detour that pulls you off a priority task.
            Field research puts full re-engagement after one at ~23 minutes (Mark, Gudith &amp; Klocke, 2008).
          </p>
          <p>
            Take a fully-loaded employee cost of <strong className="text-[#1A1A2E]">£50,000 per year</strong>, 220 working days of 7.5 hours, and you get about <strong className="text-[#1A1A2E]">£30 per hour</strong>.
            Two disruptive FACEs a day is 46 minutes of recovery daily, roughly <strong className="text-[#1A1A2E]">169 hours a year</strong>, or ~£5,100 per person.
            Across 100 employees that is <strong className="text-[#1A1A2E]">~£510,000</strong>. Most teams see far more than two, which is why we write "500K+".
          </p>
          <p>Your own number depends on salaries, hours and headcount. Run it below.</p>
        </div>
        <CostCalculator />
      </section>
    </MarketingLayout>
  );
}
